import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Building2, Check, Crown, MailCheck, Pencil, Plus, UsersRound } from "lucide-react";
import { useEffect, useState } from "react";

import { OrganizationDetail, OrganizationMember, OrganizationSummary, api, formatDate } from "../api";
import { EmptyState, ErrorState, LoadingState, MutationMessage, PageHeader, Section, StatusBadge } from "./components";

export function OrganizationPage() {
  const client = useQueryClient();
  const organizations = useQuery({ queryKey: ["organizations"], queryFn: () => api<{ organizations: OrganizationSummary[] }>("/api/v1/organizations") });
  const [selected, setSelected] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);
  const [invitationToken, setInvitationToken] = useState(readInvitationToken);
  const acceptInvitation = useMutation({
    mutationFn: () => {
      if (!invitationToken) throw new Error("邀请凭据已经清除，请重新打开邀请邮件。");
      return api<{ organizationId: string }>("/api/v1/organization-invitations/accept", {
        method: "POST",
        body: JSON.stringify({ token: invitationToken }),
      });
    },
    onSuccess: (result) => {
      setInvitationToken(null);
      setSelected(result.organizationId);
      void client.invalidateQueries({ queryKey: ["organizations"] });
    },
  });
  useEffect(() => {
    if (!selected && organizations.data?.organizations[0]) setSelected(organizations.data.organizations[0].id);
  }, [organizations.data, selected]);

  return (
    <>
      <PageHeader eyebrow="MEMBERSHIP" title="组织" description="查看你加入的组织、角色和成员。" action={<button className="primary-button" type="button" onClick={() => setCreating(!creating)}><Plus />新建组织</button>} />
      {invitationToken && (
        <section className="creation-band">
          <div><strong>接受组织邀请</strong><span>系统会校验当前账号的已验证邮箱是否与邀请一致。</span></div>
          <button className="primary-button" type="button" disabled={acceptInvitation.isPending} onClick={() => acceptInvitation.mutate()}><MailCheck />接受邀请</button>
          <MutationMessage error={acceptInvitation.error} />
        </section>
      )}
      {acceptInvitation.isSuccess && (
        <section className="creation-band">
          <div><strong>邀请已接受</strong><span>组织成员资格已经更新。</span></div>
        </section>
      )}
      {creating && <CreateOrganization onCreated={(id) => { setSelected(id); setCreating(false); }} />}
      <Section title="我的组织" description="组织为应用、环境和协作权限提供租户边界。">
        {organizations.isPending && <LoadingState />}
        {organizations.error && <ErrorState error={organizations.error} retry={() => void organizations.refetch()} />}
        {organizations.data?.organizations.length === 0 && !creating && <EmptyState icon={Building2} title="尚未加入组织" detail="创建组织后即可配置成员和开发者应用。" />}
        {(organizations.data?.organizations.length ?? 0) > 0 && (
          <div className="organization-workspace">
            <div className="organization-list" role="listbox" aria-label="组织">
              {organizations.data?.organizations.map((organization) => (
                <button className={selected === organization.id ? "active" : ""} role="option" aria-selected={selected === organization.id} key={organization.id} type="button" onClick={() => setSelected(organization.id)}>
                  <span className="avatar organization-avatar">{organization.displayName.slice(0, 1).toUpperCase()}</span>
                  <span><strong>{organization.displayName}</strong><small>{organization.slug}</small></span>
                </button>
              ))}
            </div>
            {selected && <OrganizationDetailPanel id={selected} />}
          </div>
        )}
      </Section>
    </>
  );
}

function readInvitationToken(): string | null {
  const params = new URLSearchParams(window.location.hash.replace(/^#/, ""));
  const token = params.get("invitation");
  if (token !== null) {
    window.history.replaceState(
      null,
      "",
      `${window.location.pathname}${window.location.search}#/organizations`,
    );
  }
  return token && /^[A-Za-z0-9_-]{43}$/.test(token) ? token : null;
}

function CreateOrganization({ onCreated }: { onCreated: (id: string) => void }) {
  const client = useQueryClient();
  const [slug, setSlug] = useState("");
  const [displayName, setDisplayName] = useState("");
  const create = useMutation({
    mutationFn: () => api<OrganizationDetail>("/api/v1/organizations", { method: "POST", body: JSON.stringify({ slug, displayName }) }),
    onSuccess: (value) => { void client.invalidateQueries({ queryKey: ["organizations"] }); onCreated(value.id); },
  });
  return (
    <section className="creation-band">
      <div><strong>创建组织</strong><span>Slug 创建后不可修改，只能使用字母、数字和连字符。</span></div>
      <form onSubmit={(event) => { event.preventDefault(); create.mutate(); }}>
        <input required pattern="[A-Za-z][A-Za-z0-9-]{2,47}" maxLength={48} placeholder="organization-slug" value={slug} onChange={(event) => setSlug(event.target.value)} />
        <input required maxLength={100} placeholder="组织显示名称" value={displayName} onChange={(event) => setDisplayName(event.target.value)} />
        <button className="primary-button" disabled={create.isPending} type="submit"><Plus />创建</button>
      </form>
      <MutationMessage error={create.error} />
    </section>
  );
}

function OrganizationDetailPanel({ id }: { id: string }) {
  const client = useQueryClient();
  const detail = useQuery({ queryKey: ["organization", id], queryFn: () => api<OrganizationDetail>(`/api/v1/organizations/${id}`) });
  const members = useQuery({ queryKey: ["organization-members", id], queryFn: () => api<{ members: OrganizationMember[] }>(`/api/v1/organizations/${id}/members`) });
  const [editing, setEditing] = useState(false);
  const [displayName, setDisplayName] = useState("");
  useEffect(() => setDisplayName(detail.data?.displayName ?? ""), [detail.data?.displayName]);
  const update = useMutation({
    mutationFn: () => api<OrganizationDetail>(`/api/v1/organizations/${id}`, { method: "PATCH", body: JSON.stringify({ displayName }) }),
    onSuccess: (value) => { client.setQueryData(["organization", id], value); void client.invalidateQueries({ queryKey: ["organizations"] }); setEditing(false); },
  });

  if (detail.isPending || members.isPending) return <LoadingState />;
  if (detail.error || members.error || !detail.data) return <ErrorState error={detail.error ?? members.error} />;
  return (
    <div className="organization-detail">
      <header>
        <div>
          {editing ? <form className="rename-form" onSubmit={(event) => { event.preventDefault(); update.mutate(); }}><input autoFocus required maxLength={100} value={displayName} onChange={(event) => setDisplayName(event.target.value)} /><button className="icon-button" title="保存名称" aria-label="保存名称"><Check /></button></form> : <h2>{detail.data.displayName}</h2>}
          <span>{detail.data.slug} · 创建于 {formatDate(detail.data.createdAt)}</span>
        </div>
        {!editing && detail.data.permissions.includes("organization.manage") && <button className="icon-button" title="修改名称" aria-label="修改组织名称" type="button" onClick={() => setEditing(true)}><Pencil /></button>}
      </header>
      <MutationMessage error={update.error} success={update.isSuccess && "组织名称已更新"} />
      <div className="organization-facts">
        <div><span>我的角色</span><div className="scope-list">{detail.data.currentRoles.map((role) => <StatusBadge tone={role.kind === "OWNER" ? "good" : "neutral"} key={role.id}>{role.displayName}</StatusBadge>)}</div></div>
        <div><span>权限</span><strong>{detail.data.permissions.length}</strong></div>
        <div><span>状态</span><StatusBadge tone={detail.data.status === "ACTIVE" ? "good" : "warning"}>{detail.data.status}</StatusBadge></div>
      </div>
      <div className="subsection-heading"><div><UsersRound /><strong>成员</strong></div><span>{members.data?.members.length ?? 0} 人</span></div>
      <div className="member-list">
        {members.data?.members.map((member) => (
          <div className="member-row" key={member.id}>
            <span className="avatar">{member.displayName.slice(0, 1).toUpperCase()}</span>
            <div><strong>{member.displayName}</strong><span>@{member.username} · 加入于 {formatDate(member.joinedAt)}</span></div>
            <div className="scope-list">{member.roles.map((role) => <StatusBadge tone={role.kind === "OWNER" ? "good" : "neutral"} key={role.id}>{role.kind === "OWNER" && <Crown />}{role.displayName}</StatusBadge>)}</div>
          </div>
        ))}
      </div>
    </div>
  );
}
